// GL accounts as methods of payment (migration 143).
//
// A bank / credit-card account in the chart of accounts can be what a document was paid
// from: "RBC MC 6881". Receipts print it many ways — "MC xx6881", "MASTERCARD ****6881",
// "Card ending in 6881" — so an account is recognised by
//   1. the last 4 digits of the card / account (taken from its nickname, how-receipts-
//      print-it words, or name) next to a mask or card wording on the receipt — strongest;
//   2. one of its how-receipts-print-it phrases, or its nickname, printed on the receipt;
//   3. the card brand alone (Visa, Mastercard…) when only ONE payment account of the
//      books has that brand and the receipt shows no other card's digits.
// Pure — no React, no storage. Tested in paymentAccount.test.ts.

export interface PaymentAccountLite {
  coa_id: number;
  account_name: string;
  account_number?: string | null;
  finsta_cate_id?: number | null;
  nickname?: string | null;
  payment_match?: string | null;
  alias_names?: string | null;
  is_payment_method?: number | string | null;
}

/** Category names whose accounts are methods of payment unless switched off. */
const PAYMENT_CATEGORIES = new Set(['bank', 'credit card']);

/** Listed under Method of payment? 1 / 0 on the account wins, else by its category. */
export function isPaymentAccount(a: PaymentAccountLite, cateName: string | null | undefined): boolean {
  const flag = a.is_payment_method;
  if (flag !== null && flag !== undefined && flag !== '') return Number(flag) === 1;
  return PAYMENT_CATEGORIES.has(String(cateName || '').trim().toLowerCase());
}

/** What a payment account is called in the memo / file name: nickname, else its name. */
export const paymentAccountLabel = (a: PaymentAccountLite): string =>
  String(a.nickname || '').trim() || String(a.account_name || '').trim();

const list = (s?: string | null): string[] =>
  String(s || '').split(',').map(x => x.trim()).filter(Boolean);

const norm = (s: string): string => s.toLowerCase().replace(/[•●]/g, '*').replace(/\s+/g, ' ').trim();

// ---- card brand ----

export type CardBrand = 'Visa' | 'Mastercard' | 'Amex' | 'Debit';

const BRANDS: { brand: CardBrand; re: RegExp }[] = [
  { brand: 'Mastercard', re: /\bmaster\s*card\b|\bm\/c\b|\bmc\b/i },
  { brand: 'Visa', re: /\bvisa\b/i },
  { brand: 'Amex', re: /\bamex\b|\bamerican\s+express\b/i },
  { brand: 'Debit', re: /\bdebit\b|\binterac\b/i },
];

/** The card brand an account is named / nicknamed by, or null. */
export function accountBrand(a: PaymentAccountLite): CardBrand | null {
  const hay = [a.nickname, a.account_name, a.payment_match, a.alias_names].filter(Boolean).join(' , ');
  for (const b of BRANDS) if (b.re.test(hay)) return b.brand;
  return null;
}

// ---- last digits ----

/** 4-digit groups an account is known by: "RBC MC xx6881 1011" → ["6881", "1011"]. */
export function accountLastDigits(a: PaymentAccountLite): string[] {
  const hay = [a.nickname, a.payment_match, a.account_name].filter(Boolean).join(' ');
  const out = new Set<string>();
  const re = /(?:^|[^\d])(\d{4})(?!\d)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(hay))) out.add(m[1]);
  return Array.from(out);
}

const MASK = '[x*#•●]';
const CARD_WORDS = '(?:ending(?:\\s+in)?|last\\s*(?:4|four)(?:\\s*digits)?|card\\s*(?:no\\.?|number|#)?|acct\\.?|account(?:\\s*(?:no\\.?|number|#))?|visa|master\\s*card|mc|m\\/c|amex|debit)';

/** The last 4 digits of cards / accounts printed on a receipt — only digits next to a
 *  mask ("xx6881", "****6881") or card wording ("ending in 6881", "VISA 6881"), so a
 *  phone number or an amount is not taken for a card. */
export function receiptCardDigits(text: string): string[] {
  const hay = String(text || '');
  const out = new Set<string>();
  const res = [
    new RegExp(`${MASK}{2,}[\\s-]*(\\d{4})(?!\\d)`, 'gi'),
    new RegExp(`\\b${CARD_WORDS}\\s*[:#.]?\\s*(?:${MASK}[\\s-]*)*(\\d{4})(?!\\d)`, 'gi'),
  ];
  for (const re of res) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(hay))) out.add(m[1]);
  }
  return Array.from(out);
}

// ---- matching ----

export type PaymentMatchWhy = 'digits' | 'phrase' | 'brand';
export interface PaymentAccountMatch { coaId: number; why: PaymentMatchWhy }

/** Is `phrase` printed in `hay` (both normalised), as whole words? */
function phraseHit(hay: string, phrase: string): boolean {
  const p = norm(phrase);
  if (p.length < 3) return false;
  const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s*');
  return new RegExp(`(?:^|[^a-z0-9])${esc}(?![a-z0-9])`, 'i').test(hay);
}

/** The payment account the receipt was paid from, or null when nothing (or more than
 *  one account equally) fits. `accounts` = the books' payment accounts only. */
export function matchPaymentAccount(text: string, accounts: PaymentAccountLite[]): PaymentAccountMatch | null {
  const hay = norm(String(text || ''));
  if (!hay || !accounts.length) return null;
  const printedDigits = new Set(receiptCardDigits(text));

  const scored: { a: PaymentAccountLite; score: number; why: PaymentMatchWhy }[] = [];
  for (const a of accounts) {
    const digits = accountLastDigits(a);
    if (digits.some(d => printedDigits.has(d))) { scored.push({ a, score: 100, why: 'digits' }); continue; }
    // The receipt shows a card's digits and none of them is this account's: another card.
    if (digits.length && printedDigits.size) continue;
    const phrases = [...list(a.payment_match), String(a.nickname || '').trim()].filter(Boolean);
    const hit = phrases.filter(ph => phraseHit(hay, ph)).sort((x, y) => y.length - x.length)[0];
    if (hit) scored.push({ a, score: 50 + hit.length, why: 'phrase' });
  }

  if (!scored.length) {
    // Brand alone: only when exactly one account of the books has the receipt's brand.
    const brands = BRANDS.filter(b => b.re.test(hay)).map(b => b.brand);
    if (brands.length !== 1) return null;
    const same = accounts.filter(a => accountBrand(a) === brands[0]
      && !(accountLastDigits(a).length && printedDigits.size));
    return same.length === 1 ? { coaId: Number(same[0].coa_id), why: 'brand' } : null;
  }
  scored.sort((x, y) => y.score - x.score);
  if (scored.length > 1 && scored[1].score === scored[0].score) return null;   // can't tell which
  return { coaId: Number(scored[0].a.coa_id), why: scored[0].why };
}

export const PAYMENT_MATCH_WHY_LABEL: Record<PaymentMatchWhy, string> = {
  digits: 'card / account digits on the receipt',
  phrase: 'how the receipt prints it',
  brand: 'card brand on the receipt',
};
